import type {
  CardDirection,
  CardDirectionSchedule,
  CardGradeOutcome,
  CardTargetSnapshot,
  IntervalStage,
  MeaningPracticeState,
  ReviewAttemptOutcome,
  ReviewEventKind,
  ScheduleSnapshot,
  SchedulerState,
  VocabularySnapshot,
} from "../vocabulary/types.ts";
import { SCHEDULER_POLICY_VERSION } from "../vocabulary/types.ts";

export const DAY_MS = 24 * 60 * 60 * 1000;
export const INTERVAL_DAYS = [1, 3, 7, 14, 30] as const;
export const MIN_STAGE = 0;
export const MAX_STAGE = 4;
export const REVIEW_HISTORY_RETENTION = 5_000;
export const UTC_INSTANT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/;

export { SCHEDULER_POLICY_VERSION };

export function isUtcInstant(value: string): boolean {
  if (!UTC_INSTANT_PATTERN.test(value)) {
    return false;
  }
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds);
}

export function instantMs(value: string): number | null {
  if (!isUtcInstant(value)) {
    return null;
  }
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) ? milliseconds : null;
}

export function compareIds(left: string, right: string): number {
  if (left < right) {
    return -1;
  }
  if (left > right) {
    return 1;
  }
  return 0;
}

export function compareInstants(left: string, right: string): number {
  const leftMs = instantMs(left);
  const rightMs = instantMs(right);
  if (leftMs == null || rightMs == null) {
    return compareIds(left, right);
  }
  if (leftMs < rightMs) {
    return -1;
  }
  if (leftMs > rightMs) {
    return 1;
  }
  return 0;
}

export function isAtOrAfter(left: string, right: string): boolean {
  return compareInstants(left, right) >= 0;
}

export function addDays(iso: string, days: number): string {
  const milliseconds = instantMs(iso);
  if (milliseconds == null) {
    throw new Error("Invalid UTC instant.");
  }
  return new Date(milliseconds + days * DAY_MS).toISOString();
}

export function minInstant(left: string, right: string): string {
  return compareInstants(left, right) <= 0 ? left : right;
}

export function maxInstant(left: string, right: string): string {
  return compareInstants(left, right) >= 0 ? left : right;
}

export function isDue(dueAt: string, now: string): boolean {
  return isAtOrAfter(now, dueAt);
}

export function isSpacedSince(lastExposureAt: string | null, presentedAt: string): boolean {
  if (lastExposureAt == null) {
    return false;
  }
  return isAtOrAfter(presentedAt, addDays(lastExposureAt, 1));
}

export function isClockBackward(
  lastObservedAt: string | null,
  presentedAt: string,
  completedAt: string,
): boolean {
  if (!isUtcInstant(presentedAt) || !isUtcInstant(completedAt)) {
    return true;
  }
  if (compareInstants(completedAt, presentedAt) < 0) {
    return true;
  }
  if (lastObservedAt != null && compareInstants(presentedAt, lastObservedAt) < 0) {
    return true;
  }
  if (lastObservedAt != null && compareInstants(completedAt, lastObservedAt) < 0) {
    return true;
  }
  return false;
}

export function asStage(value: number): IntervalStage {
  if (value <= MIN_STAGE) {
    return 0;
  }
  if (value >= MAX_STAGE) {
    return 4;
  }
  return value as IntervalStage;
}

export function intervalDaysForStage(stage: IntervalStage): number {
  return INTERVAL_DAYS[stage];
}

export function initialCardSchedule(dueAt: string): CardDirectionSchedule {
  return {
    stage: 0,
    dueAt,
    lastAssessmentAt: null,
    lastPresentedAt: null,
    presentationOrdinal: null,
  };
}

export function initialPracticeState(meaningId: string, dueAt: string): MeaningPracticeState {
  return {
    meaningId,
    stage: 0,
    dueAt,
    lastExposureAt: null,
    lastAssessmentAt: null,
    lastExerciseId: null,
    stateRevision: 0,
    flaggedExerciseIds: [],
    cardSchedules: {
      hide_meaning: initialCardSchedule(dueAt),
      hide_word: initialCardSchedule(dueAt),
    },
  };
}

export function practiceStateFor(
  snapshot: VocabularySnapshot,
  meaningId: string,
): MeaningPracticeState {
  return (
    snapshot.practiceStates.find((state) => state.meaningId === meaningId) ??
    initialPracticeState(meaningId, snapshot.schedulerState.lastObservedAt ?? snapshot.meanings.find((item) => item.id === meaningId)?.createdAt ?? "1970-01-01T00:00:00.000Z")
  );
}

export function scheduleSnapshotOf(state: MeaningPracticeState): ScheduleSnapshot {
  return {
    stage: state.stage,
    dueAt: state.dueAt,
    lastExposureAt: state.lastExposureAt,
    lastAssessmentAt: state.lastAssessmentAt,
    lastExerciseId: state.lastExerciseId,
    stateRevision: state.stateRevision,
  };
}

export interface PresentationClassification {
  wasDue: boolean;
  wasSpaced: boolean;
  isFirstAssessment: boolean;
}

export function classifyPresentation(
  state: MeaningPracticeState,
  presentedAt: string,
): PresentationClassification {
  return {
    wasDue: isDue(state.dueAt, presentedAt),
    wasSpaced: isSpacedSince(state.lastExposureAt, presentedAt),
    isFirstAssessment: state.lastAssessmentAt == null,
  };
}

export function compareDueThenId(left: MeaningPracticeState, right: MeaningPracticeState): number {
  const byDue = compareInstants(left.dueAt, right.dueAt);
  if (byDue !== 0) {
    return byDue;
  }
  return compareIds(left.meaningId, right.meaningId);
}

export function compareExtraPractice(left: MeaningPracticeState, right: MeaningPracticeState): number {
  if (left.lastExposureAt == null && right.lastExposureAt != null) {
    return -1;
  }
  if (left.lastExposureAt != null && right.lastExposureAt == null) {
    return 1;
  }
  if (left.lastExposureAt != null && right.lastExposureAt != null) {
    const byExposure = compareInstants(left.lastExposureAt, right.lastExposureAt);
    if (byExposure !== 0) {
      return byExposure;
    }
  }
  return compareDueThenId(left, right);
}

export function avoidLastWhenPossible<T extends { meaningId: string }>(
  pool: readonly T[],
  lastPracticedMeaningId: string | null,
): T[] {
  if (lastPracticedMeaningId == null || pool.length <= 1) {
    return [...pool];
  }
  const withoutLast = pool.filter((item) => item.meaningId !== lastPracticedMeaningId);
  return withoutLast.length > 0 ? withoutLast : [...pool];
}

function minBy<T>(items: readonly T[], compare: (left: T, right: T) => number): T | undefined {
  let chosen = items[0];
  for (let index = 1; index < items.length; index += 1) {
    const item = items[index]!;
    if (compare(item, chosen!) < 0) {
      chosen = item;
    }
  }
  return chosen;
}

export type SelectionBucket = "scheduled" | "extra";

export interface MeaningSelection {
  meaningId: string;
  bucket: SelectionBucket;
}

export function selectDueMeaning(
  candidates: readonly MeaningPracticeState[],
  now: string,
  lastPracticedMeaningId: string | null,
): MeaningSelection | null {
  const due = candidates.filter((state) => isDue(state.dueAt, now));
  const chosen = minBy(avoidLastWhenPossible(due, lastPracticedMeaningId), compareDueThenId);
  return chosen ? { meaningId: chosen.meaningId, bucket: "scheduled" } : null;
}

export function selectExtraMeaning(
  candidates: readonly MeaningPracticeState[],
  now: string,
  lastPracticedMeaningId: string | null,
): MeaningSelection | null {
  const due = selectDueMeaning(candidates, now, lastPracticedMeaningId);
  if (due) {
    return due;
  }
  if (candidates.length === 0) {
    return null;
  }
  const chosen = minBy(avoidLastWhenPossible(candidates, lastPracticedMeaningId), compareExtraPractice);
  return chosen ? { meaningId: chosen.meaningId, bucket: "extra" } : null;
}

export function selectPracticeMeaning(
  candidates: readonly MeaningPracticeState[],
  now: string,
  lastPracticedMeaningId: string | null,
  allowExtra: boolean,
): MeaningSelection | null {
  const due = selectDueMeaning(candidates, now, lastPracticedMeaningId);
  if (due) {
    return due;
  }
  if (!allowExtra) {
    return null;
  }
  return selectExtraMeaning(candidates, now, lastPracticedMeaningId);
}

export function nextReviewAt(
  candidates: readonly MeaningPracticeState[],
  now: string,
): string | null {
  const upcoming = candidates.filter((state) => !isDue(state.dueAt, now));
  return minBy(upcoming, compareDueThenId)?.dueAt ?? null;
}

export interface ScheduleTransitionInput {
  eventKind: ReviewEventKind;
  outcome: ReviewAttemptOutcome | null;
  hintUsed: boolean;
  classification: PresentationClassification;
  completedAt: string;
}

export function nextSchedule(
  state: MeaningPracticeState,
  input: ScheduleTransitionInput,
): Pick<MeaningPracticeState, "stage" | "dueAt"> {
  if (input.eventKind !== "assessment") {
    return { stage: state.stage, dueAt: state.dueAt };
  }
  const unsuccessful =
    input.outcome === "incorrect" || input.outcome === "unknown" || input.hintUsed;
  const tomorrow = addDays(input.completedAt, 1);
  const { wasDue, wasSpaced, isFirstAssessment } = input.classification;

  if (unsuccessful) {
    return {
      stage: 0,
      dueAt: wasDue ? tomorrow : minInstant(state.dueAt, tomorrow),
    };
  }

  if (input.outcome === "correct") {
    if (isFirstAssessment) {
      return {
        stage: 0,
        dueAt: wasDue ? tomorrow : state.dueAt,
      };
    }
    if (wasDue) {
      if (wasSpaced) {
        const stage = asStage(state.stage + 1);
        return {
          stage,
          dueAt: addDays(input.completedAt, intervalDaysForStage(stage)),
        };
      }
      return { stage: state.stage, dueAt: tomorrow };
    }
    return { stage: state.stage, dueAt: state.dueAt };
  }

  return { stage: state.stage, dueAt: state.dueAt };
}

export function resetScheduleForNewContent(
  state: MeaningPracticeState,
  now: string,
): MeaningPracticeState {
  return {
    ...state,
    stage: 0,
    dueAt: now,
    lastAssessmentAt: null,
    lastExerciseId: null,
    flaggedExerciseIds: [],
    lastExposureAt: now,
    stateRevision: state.stateRevision + 1,
    cardSchedules: {
      hide_meaning: initialCardSchedule(now),
      hide_word: initialCardSchedule(now),
    },
  };
}

export type CardHeldReason = "first_assessment" | "early" | "context" | "recent_exposure";

export interface CardScheduleTransition {
  stage: IntervalStage;
  dueAt: string;
  heldReason: CardHeldReason | null;
}

export function classifyCardPresentation(
  state: MeaningPracticeState,
  direction: CardDirection,
  presentedAt: string,
): PresentationClassification {
  const card = state.cardSchedules[direction];
  return {
    wasDue: isDue(card.dueAt, presentedAt),
    wasSpaced: isSpacedSince(state.lastExposureAt, presentedAt),
    isFirstAssessment: card.lastAssessmentAt == null,
  };
}

export function cardTargetSnapshot(
  state: MeaningPracticeState,
  direction: CardDirection,
): CardTargetSnapshot {
  const card = state.cardSchedules[direction];
  return {
    stage: card.stage,
    dueAt: card.dueAt,
    lastAssessmentAt: card.lastAssessmentAt,
    lastPresentedAt: card.lastPresentedAt,
    presentationOrdinal: card.presentationOrdinal,
    lastExposureAt: state.lastExposureAt,
    stateRevision: state.stateRevision,
  };
}

export function isCardClockBackward(input: {
  knownObservedAt: string | null;
  presentedAt: string;
  actionAt: string;
  previousActionAt: string | null;
}): boolean {
  if (!isUtcInstant(input.presentedAt) || !isUtcInstant(input.actionAt)) {
    return true;
  }
  if (compareInstants(input.actionAt, input.presentedAt) < 0) {
    return true;
  }
  if (input.previousActionAt != null) {
    if (!isUtcInstant(input.previousActionAt) || compareInstants(input.actionAt, input.previousActionAt) < 0) {
      return true;
    }
  }
  if (input.knownObservedAt != null && compareInstants(input.actionAt, input.knownObservedAt) < 0) {
    return true;
  }
  return false;
}

export function nextCardSchedule(
  card: CardDirectionSchedule,
  input: {
    outcome: CardGradeOutcome;
    contextUsed: boolean;
    wasDue: boolean;
    wasSpaced: boolean;
    isFirstAssessment: boolean;
    actionAt: string;
  },
): CardScheduleTransition {
  const tomorrow = addDays(input.actionAt, 1);
  if (input.outcome === "incorrect") {
    return {
      stage: 0,
      dueAt: input.wasDue ? tomorrow : minInstant(card.dueAt, tomorrow),
      heldReason: null,
    };
  }
  if (input.isFirstAssessment) {
    return {
      stage: 0,
      dueAt: input.wasDue ? tomorrow : card.dueAt,
      heldReason: "first_assessment",
    };
  }
  if (!input.wasDue) {
    return {
      stage: card.stage,
      dueAt: card.dueAt,
      heldReason: input.contextUsed ? "context" : "early",
    };
  }
  if (input.contextUsed) {
    return { stage: card.stage, dueAt: tomorrow, heldReason: "context" };
  }
  if (!input.wasSpaced) {
    return { stage: card.stage, dueAt: tomorrow, heldReason: "recent_exposure" };
  }
  const stage = asStage(card.stage + 1);
  return {
    stage,
    dueAt: addDays(input.actionAt, intervalDaysForStage(stage)),
    heldReason: null,
  };
}

export function compareCardPresentation(
  direction: CardDirection,
  left: MeaningPracticeState,
  right: MeaningPracticeState,
): number {
  const leftCard = left.cardSchedules[direction];
  const rightCard = right.cardSchedules[direction];
  if (leftCard.lastPresentedAt == null && rightCard.lastPresentedAt != null) {
    return -1;
  }
  if (leftCard.lastPresentedAt != null && rightCard.lastPresentedAt == null) {
    return 1;
  }
  if (leftCard.lastPresentedAt != null && rightCard.lastPresentedAt != null) {
    const byTime = compareInstants(leftCard.lastPresentedAt, rightCard.lastPresentedAt);
    if (byTime !== 0) {
      return byTime;
    }
    const leftOrdinal = leftCard.presentationOrdinal ?? -1;
    const rightOrdinal = rightCard.presentationOrdinal ?? -1;
    if (leftOrdinal !== rightOrdinal) {
      return leftOrdinal - rightOrdinal;
    }
  }
  const byDue = compareInstants(leftCard.dueAt, rightCard.dueAt);
  if (byDue !== 0) {
    return byDue;
  }
  return compareIds(left.meaningId, right.meaningId);
}

export function selectCardMeaning(
  candidates: readonly MeaningPracticeState[],
  direction: CardDirection,
  lastPresentedMeaningId: string | null,
): MeaningPracticeState | undefined {
  return minBy(avoidLastWhenPossible(candidates, lastPresentedMeaningId), (left, right) =>
    compareCardPresentation(direction, left, right),
  );
}

export function cardHeldExplanation(reason: CardHeldReason, wasDue: boolean): string {
  switch (reason) {
    case "first_assessment":
      return wasDue
        ? "First correct self-check in this direction. The stage stays at 0, and the next review is in 24 hours."
        : "First correct self-check before this direction was due. The stage and next review stay the same.";
    case "early":
      return "This direction was not due yet, so the stage and next review stay the same.";
    case "context":
      return wasDue
        ? "You asked for context, so this success keeps the current stage. The next review is in 24 hours."
        : "You asked for context before this direction was due, so the stage and next review stay the same.";
    case "recent_exposure":
      return "This meaning was seen in the last 24 hours, so this success keeps the current stage. The next review is in 24 hours.";
  }
}

export function clearObsoleteExerciseRefs(
  state: MeaningPracticeState,
  validExerciseIds: ReadonlySet<string>,
): MeaningPracticeState {
  const lastExerciseId =
    state.lastExerciseId != null && validExerciseIds.has(state.lastExerciseId)
      ? state.lastExerciseId
      : null;
  const flaggedExerciseIds = state.flaggedExerciseIds.filter((id) => validExerciseIds.has(id));
  if (lastExerciseId === state.lastExerciseId && flaggedExerciseIds.length === state.flaggedExerciseIds.length) {
    return state;
  }
  return {
    ...state,
    lastExerciseId,
    flaggedExerciseIds,
    stateRevision: state.stateRevision + 1,
  };
}

export function supportedSchedulerState(state: SchedulerState): boolean {
  return state.schedulerVersion === SCHEDULER_POLICY_VERSION;
}
